import React, {
    useState,
    useImperativeHandle,
    forwardRef,
    useRef,
    useEffect,
} from 'react';
import PropTypes from 'prop-types';
import DotLoader from '@/common/dot-loader';
import { useTranslation } from 'react-i18next';
import {
    useVerifiedAt,
    requestVerification,
    completeVerification,
} from '@/hooks/api/customer';

import { getE164PhoneNumberFromCountryAndNationalPhoneNumber } from '@/common/helpers/phone_number';
import { PhoneNumberInputWithValidation as DesktopPhoneNumberInputWithValidation } from '@/desktop/common/inputs/phone-number';
import { PhoneNumberInputWithValidation as MobilePhoneNumberInputWithValidation } from '@/mobile/common/inputs/phone-number';

const CustomerVerificationForm = forwardRef(({ onComplete }, ref) => {
    const [t] = useTranslation();
    const verifiedAt = useVerifiedAt();
    const [code, setCode] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isCodeResending, setIsCodeResending] = useState(false);

    const [phone_number, setPhoneNumber] = useState(
        Mobi2Go.Customer.getPhoneNumber()
    );

    const [isInitialPhoneNumberValid, setIsInitialPhoneNumberValid] = useState(
        true
    );
    const [newPhoneNumber, setNewPhoneNumber] = useState('');
    const [phoneNumberCountry, setPhoneNumberCountry] = useState(
        Mobi2Go.headoffice.country_iso3166_alpha2
    );
    const [phoneNumberErrorMessage, setPhoneNumberErrorMessage] = useState('');

    const [resendTimeout, setResendTimeout] = useState(10);
    const phoneNumberErrorRef = useRef(null);

    useEffect(() => {
        Mobi2Go.Customer.bind({
            update: updatePhoneNumber,
            logIn: updatePhoneNumber,
        });

        return () =>
            Mobi2Go.Customer.unbind({
                update: updatePhoneNumber,
                logIn: updatePhoneNumber,
            });
    }, []);

    const updatePhoneNumber = () => {
        setPhoneNumber(Mobi2Go.Customer.getPhoneNumber());
    };

    const checkNumberAndRequestCode = () => {
        if (
            Mobi2Go.Customer.getPhoneNumber() &&
            Mobi2Go.Customer.getPhoneNumber().charAt(0) !== '+'
        ) {
            setIsInitialPhoneNumberValid(false);
            return;
        }

        setupResendButtonTimeout();
        requestVerification();
    };

    const setupResendButtonTimeout = () => {
        setIsCodeResending(true);

        const timeoutInterval = setInterval(() => {
            setResendTimeout(timeout => {
                const newTimeout = timeout - 1;
                if (newTimeout === 0) {
                    clearInterval(timeoutInterval);
                    setIsCodeResending(false);
                    return 10;
                }
                return newTimeout;
            });
        }, 1000);
    };

    useImperativeHandle(
        ref,
        () => ({
            checkNumberAndRequestCode,
        }),
        []
    );

    const onSubmit = () => {
        setIsLoading(true);
        completeVerification(code)
            .then(() => {
                setIsLoading(false);
                Mobi2Go.UI.trigger('smsVerificationCompleted');
            })
            .catch(() => {
                setIsLoading(false);
                Mobi2Go.UI.centeredAlert({
                    title: t('customer.verification:invalid_code_alert_title'),
                    body: t('customer.verification:invalid_code_alert_body'),
                });
            });
    };

    const saveToServer = () => {
        setIsLoading(true);
        let phoneNumberValidated = newPhoneNumber;

        try {
            phoneNumberValidated = getE164PhoneNumberFromCountryAndNationalPhoneNumber(
                phoneNumberCountry,
                newPhoneNumber
            );
        } catch (e) {
            handleUpdateError('Invalid phone_number number');
            setIsLoading(false);
            return;
        }

        try {
            Mobi2Go.Customer.update(
                {
                    phone_number: phoneNumberValidated,
                },
                (status, error) => {
                    if (status === 'error' && error) {
                        handleUpdateError(error);
                    } else {
                        updatePageStateWithNewNumber();
                    }
                    setIsLoading(false);
                }
            );
        } catch (error) {
            setIsLoading(false);
        }
    };

    const updatePageStateWithNewNumber = () => {
        setPhoneNumber(Mobi2Go.Customer.getPhoneNumber());
        setIsInitialPhoneNumberValid(true);
        checkNumberAndRequestCode();
    };

    const animatePhoneNumberError = () => {
        if (this.phone_number_error_ref) {
            Mobi2Go.UI.helpers.setAnimation(
                phoneNumberErrorRef.current,
                'rock',
                true
            );
        }
    };

    const handleUpdateError = error => {
        if (error === 'Invalid phone_number number') {
            setPhoneNumberErrorMessage('Invalid phone number', () => {
                if (
                    phoneNumberErrorMessage !== '' &&
                    phoneNumberErrorRef.current
                ) {
                    animatePhoneNumberError();
                }
            });
        } else {
            setPhoneNumberErrorMessage(JSON.stringify(error));
        }
    };

    if (verifiedAt) {
        return (
            <div className="m2g-customer-verification">
                <p className="m2g-customer-verification-explanation">
                    {t('customer.verification:verified-explanation')}
                </p>
                <button
                    type="button"
                    id="m2g-customer-verification-done-button"
                    className="m2g-customer-verification-done-button m2g-button m2g-button--primary m2g-button--fat"
                    tabIndex="0"
                    onKeyDown={() => {}}
                    onClick={onComplete}
                >
                    {t('customer.verification:done-button-label')}
                </button>
            </div>
        );
    }

    return (
        <div className="m2g-customer-verification">
            {!isInitialPhoneNumberValid ? (
                <>
                    <p className="m2g-customer-verification-explanation">
                        {t('customer:register_invalid_phone_title')}.{' '}
                        {t('customer:register_invalid_phone_body')}
                    </p>
                    {Mobi2Go.app.getPlatform() === 'web' ? (
                        <DesktopPhoneNumberInputWithValidation
                            value={newPhoneNumber}
                            country={phoneNumberCountry}
                            error={phoneNumberErrorMessage}
                            placeholder={t(
                                'customer.details:phone_number_placeholder'
                            )}
                            containerClassName="m2g-customer-verification"
                            onChange={phoneNumber =>
                                setNewPhoneNumber(phoneNumber)
                            }
                            onCountryChange={country =>
                                setPhoneNumberCountry(country)
                            }
                            onBlur={() => {}}
                            errorRef={phoneNumberErrorRef}
                        />
                    ) : (
                        <MobilePhoneNumberInputWithValidation
                            value={newPhoneNumber}
                            country={phoneNumberCountry}
                            error={phoneNumberErrorMessage}
                            placeholder={t(
                                'customer.details:phone_number_placeholder'
                            )}
                            containerClassName="m2g-customer-verification"
                            onChange={phoneNumber =>
                                setNewPhoneNumber(phoneNumber)
                            }
                            onCountryChange={country =>
                                setPhoneNumberCountry(country)
                            }
                            onBlur={() => {}}
                            errorRef={phoneNumberErrorRef}
                        />
                    )}
                    <button
                        type="button"
                        id="m2g-customer-verification-submit-button"
                        className="m2g-customer-verification-submit-button m2g-button m2g-button--primary m2g-button--fat"
                        tabIndex="0"
                        onKeyDown={() => {}}
                        onClick={saveToServer}
                        disabled={isLoading || isCodeResending}
                    >
                        {isLoading || isCodeResending ? (
                            <DotLoader dotCharacter="•" />
                        ) : (
                            t('customer.verification:submit-button-label')
                        )}
                    </button>
                </>
            ) : (
                <>
                    <p className="m2g-customer-verification-explanation">
                        {t('customer.verification:explanation', {
                            phone_number,
                        })}
                    </p>
                    <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        className="m2g-input m2g-customer-verification-code-input"
                        id="m2g-customer-verification-code-input"
                        placeholder={t(
                            'customer.verification:input-placeholder'
                        )}
                        autoComplete="one-time-code"
                        value={code}
                        onChange={e => {
                            const {
                                target: { value },
                            } = e;
                            setCode(value);
                        }}
                    />
                    <button
                        type="button"
                        id="m2g-customer-verification-submit-button"
                        className="m2g-customer-verification-submit-button m2g-button m2g-button--primary m2g-button--fat"
                        tabIndex="0"
                        onKeyDown={() => {}}
                        onClick={onSubmit}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <DotLoader dotCharacter="•" />
                        ) : (
                            t('customer.verification:submit-button-label')
                        )}
                    </button>
                    <div className={'m2g-customer-verification'}>
                        <div className="m2g-customer-verification-resend-status">
                            {!isCodeResending
                                ? t('customer.verification:resend-status')
                                : t('customer.verification:code_sent')}
                        </div>
                        <div
                            className={
                                isCodeResending
                                    ? 'm2g-customer-verification-invisibility-cloak'
                                    : 'm2g-customer-verification'
                            }
                        >
                            <button
                                type="button"
                                id="m2g-customer-verification-resend-button"
                                className="m2g-customer-verification-resend-button m2g-button m2g-button--bare"
                                tabIndex="0"
                                onKeyDown={() => {}}
                                onClick={checkNumberAndRequestCode}
                                disabled={isCodeResending}
                            >
                                {t('customer.verification:resend-button-label')}
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
});

CustomerVerificationForm.propTypes = {
    onComplete: PropTypes.func.isRequired,
};

CustomerVerificationForm.displayName = 'CustomerVerificationForm';

export default CustomerVerificationForm;
